import React from "react";
import { useTranslation } from "react-i18next";
import Tooltip from "@/components/Common/Tooltip";
import InputCounter from "@/components/Common/Input/InputCounter";
import { formatMoney } from "@/utils/generalUtilities";
import {
  EmployeeContributionData,
  ContributionCalculations,
} from "../types/contributionTypes";
import { hasEmployerMatching } from "../utils/contributionUtils";
import EmployerMatchThreshold from "./EmployerMatchThreshold";

interface EmployeeContributionFormProps {
  employeeContribution: number;
  employerContribution: number;
  employeeInformation: EmployeeContributionData;
  benefitContributionLimit: string;
  calculations: ContributionCalculations;
  onContributionChange: (name: string, value: number) => void;
  serviceChargeAmount?: number;
  serviceChargePaidBy?: string;
}

const EmployeeContributionForm: React.FC<EmployeeContributionFormProps> = ({
  employeeContribution,
  employerContribution,
  employeeInformation,
  benefitContributionLimit,
  calculations,
  onContributionChange,
  serviceChargeAmount = 0,
  serviceChargePaidBy,
}) => {
  const { t } = useTranslation();
  const { EmployerMaxMatch } = employeeInformation;
  const { perPaycheckEmployeeDeduction, perPaycheckEmployerContribution } =
    calculations;

  const hasMatching = hasEmployerMatching(EmployerMaxMatch);

  return (
    <div className="text-[18px] shadow-introBox font-outfit bg-white w-full rounded-[16px]">
      <div className="px-[16px] py-[32px] md:p-[32px]">
        <div className="mb-[20px] h-full">
          <p>
            {t(
              "onboarding.contributions.employee_form.salary_contribution_question",
            )}
          </p>
          {hasMatching && (
            <div className="flex space-x-2 leading-[127%] items-center">
              <p className="text-[#8B8B8B] text-[16px]">
                {t(
                  "onboarding.contributions.employee_form.annual_maximum_limit",
                  {
                    limit: formatMoney(Number(benefitContributionLimit)),
                  },
                )}
              </p>
              <Tooltip
                text={t(
                  "onboarding.contributions.form_tooltips.benefit_contribution_limit",
                )}
              />
            </div>
          )}
        </div>

        <div
          style={{
            boxShadow:
              "0px 4px 16px 0px rgba(0, 0, 0, 0.04), 0px 2px 3px 0px rgba(0, 0, 0, 0.05)",
          }}
          className="w-full p-[8px] border-[1px] border-[#E2E2E2] rounded-[12px]"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-[24px]">
            <div>
              <InputCounter
                label={t(
                  "onboarding.contributions.employee_form.your_contribution_label",
                )}
                name="employeeContribution"
                initialValue={employeeContribution}
                min={0}
                step={1}
                onChange={onContributionChange}
                isLong
                suffix="%"
                max={100}
              />
            </div>
            <div className="py-[8px] leading-[127%]">
              {hasMatching && (
                <>
                  <p className="text-[#8B8B8B] text-[16px]">
                    {t(
                      "onboarding.contributions.employee_form.employer_will_add",
                    )}
                  </p>
                  <div>
                    {employerContribution}%{" "}
                    <span>
                      {employerContribution >= EmployerMaxMatch &&
                        `(${t(
                          "onboarding.contributions.employee_form.max_label",
                        )})`}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {hasMatching && (
        <EmployerMatchThreshold
          employerMatch={EmployerMaxMatch}
          employerContribution={employerContribution}
        />
      )}

      <div className="flex flex-col space-y-[16px] px-[16px] py-[32px] md:p-[32px] bg-[#F9F9F9] border-t-[0.5px] rounded-b-[16px]">
        <div className="flex justify-between">
          <div className="leading-[127%] flex items-center">
            {t(
              "onboarding.contributions.employee_form.approx_deduction_per_paycheck",
            )}{" "}
            <span className="ml-1">
              <Tooltip
                text={t(
                  "onboarding.contributions.form_tooltips.approx_employee_deductions",
                )}
              />
            </span>
          </div>
          <p className="font-normal text-[18px] leading-[127%] ml-2">
            ${formatMoney(Math.round(perPaycheckEmployeeDeduction))}
          </p>
        </div>

        {hasMatching && (
          <div className="flex justify-between">
            <div className="leading-[127%] flex space-x-2 items-center">
              {t(
                "onboarding.contributions.employee_form.approx_employer_matching",
              )}
              {"  "}
              <span className="ml-1">
                <Tooltip
                  text={t(
                    "onboarding.contributions.form_tooltips.approx_employer_matching_deductions",
                  )}
                />
              </span>
            </div>
            <p className="font-normal text-[18px] ml-2">
              + ${formatMoney(Math.round(perPaycheckEmployerContribution))}
            </p>
          </div>
        )}

        {serviceChargePaidBy === "Employee" && serviceChargeAmount > 0 && (
          <div className="flex justify-between">
            <div className="leading-[127%] flex items-center">
              Platform service fee
            </div>
            <p className="font-normal text-[18px] ml-2">
              + ${formatMoney(serviceChargeAmount)}
            </p>
          </div>
        )}

        <div className="flex justify-between">
          <div className="font-semibold leading-[127%] flex items-center">
            {t(
              "onboarding.contributions.employee_form.total_deduction_per_cycle",
            )}{" "}
            <span className="ml-1">
              <Tooltip
                text={t(
                  "onboarding.contributions.form_tooltips.approx_total_deductions",
                )}
              />
            </span>
          </div>
          <p className="font-semibold text-[18px] ml-2">
            $
            {formatMoney(
              Math.round(perPaycheckEmployeeDeduction) +
                Math.round(perPaycheckEmployerContribution) +
                (serviceChargePaidBy === "Employee" ? serviceChargeAmount : 0),
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmployeeContributionForm;
